// Standard Calyvra "Book of Business" dashboard: product cards (ACA / Medicare /
// Dental / Vision / Life), general metrics, combinable filters and a Missing
// Documents footer sorted by due date.
import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import portalApi from '../lib/api';
import {
  HeartPulse, ShieldCheck, Smile, Eye, LifeBuoy, Loader2, AlertCircle,
  CalendarClock, X, LayoutGrid
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const CARD_META = {
  ACA: { icon: HeartPulse, color: 'bg-sky-100 text-sky-600', ring: 'hover:border-sky-300' },
  Medicare: { icon: ShieldCheck, color: 'bg-indigo-100 text-indigo-600', ring: 'hover:border-indigo-300' },
  Dental: { icon: Smile, color: 'bg-teal-100 text-teal-600', ring: 'hover:border-teal-300' },
  Vision: { icon: Eye, color: 'bg-amber-100 text-amber-600', ring: 'hover:border-amber-300' },
  Life: { icon: LifeBuoy, color: 'bg-rose-100 text-rose-600', ring: 'hover:border-rose-300' },
};

const NONE = '__all__';
const todayISO = () => new Date().toISOString().slice(0, 10);

export function StandardDashboard({ workspaceId, selectedYear, years, onYearChange, userName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState([]);
  const [agents, setAgents] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState({ agent_id: '', product: '', carrier: '', state: '', policy_status: '' });

  useEffect(() => {
    portalApi.getAgents().then(setAgents).catch(() => {});
    portalApi.getConstants().then((c) => setStatuses(c.policy_statuses || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true); setLoadError(false);
    try {
      const [bob, md] = await Promise.all([
        portalApi.getBookOfBusiness({ workspace_id: workspaceId, coverage_year_id: selectedYear, ...filters }),
        portalApi.getMissingDocuments(workspaceId),
      ]);
      setData(bob);
      setMissing(md.items || []);
    } catch (err) { console.error(err); setLoadError(true); } finally { setLoading(false); }
  }, [workspaceId, selectedYear, filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v === NONE ? '' : v }));
  const clearFilters = () => setFilters({ agent_id: '', product: '', carrier: '', state: '', policy_status: '' });
  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="space-y-8" data-testid="standard-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <LayoutGrid className="h-6 w-6 text-sky-600" />
            <h1 className="text-2xl font-bold text-slate-900">Book of Business</h1>
          </div>
          <p className="text-slate-500 mt-1">Welcome back, {userName}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear || ''} onValueChange={onYearChange}>
            <SelectTrigger className="w-[180px]" data-testid="year-selector">
              <CalendarClock className="h-4 w-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select year" />
            </SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button asChild data-testid="new-client-btn"><Link to="/portal/clients/new">New Client</Link></Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200" data-testid="bob-filters">
        <div className="flex flex-wrap items-end gap-3">
          <FilterSelect label="Agent" value={filters.agent_id} onChange={(v) => setF('agent_id', v)} testid="filter-agent"
            options={agents.map((a) => ({ value: a.id, label: a.name }))} />
          <FilterSelect label="Product" value={filters.product} onChange={(v) => setF('product', v)} testid="filter-product"
            options={(data?.filters?.products || []).map((p) => ({ value: p, label: p }))} />
          <FilterSelect label="Carrier" value={filters.carrier} onChange={(v) => setF('carrier', v)} testid="filter-carrier"
            options={(data?.filters?.carriers || []).map((c) => ({ value: c, label: c }))} />
          <FilterSelect label="State" value={filters.state} onChange={(v) => setF('state', v)} testid="filter-state"
            options={(data?.filters?.states || []).map((s) => ({ value: s, label: s }))} />
          <FilterSelect label="Policy Status" value={filters.policy_status} onChange={(v) => setF('policy_status', v)} testid="filter-status"
            options={statuses.map((s) => ({ value: s, label: s }))} />
          {activeFilterCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-btn" className="text-slate-500">
              <X className="h-4 w-4 mr-1" /> Clear ({activeFilterCount})
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div>
      ) : loadError ? (
        <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="dashboard-error">
          <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
          <p className="text-slate-700 font-medium">We couldn't load your dashboard</p>
          <Button onClick={load} className="mt-4" data-testid="dashboard-retry-btn">Try again</Button>
        </div>
      ) : (
        <>
          {/* Product cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="product-cards">
            {(data?.cards || []).map((c) => {
              const meta = CARD_META[c.category] || { icon: LayoutGrid, color: 'bg-slate-100 text-slate-600', ring: 'hover:border-slate-300' };
              const Icon = meta.icon;
              const goto = (status) => navigate(`/portal/clients?category=${encodeURIComponent(c.category)}${status ? `&status=${status}` : ''}`);
              return (
                <div key={c.category} onClick={() => goto()} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') goto(); }}
                  className={`bg-white rounded-xl p-5 border border-slate-200 cursor-pointer transition-all hover:shadow-md ${meta.ring}`}
                  data-testid={`product-card-${c.category.toLowerCase()}`}>
                  <div className={`h-10 w-10 rounded-lg ${meta.color} flex items-center justify-center mb-3`}><Icon className="h-5 w-5" /></div>
                  <p className="font-semibold text-slate-900">{c.category}</p>
                  <div className="mt-3 flex items-center gap-4">
                    <button onClick={(e) => { e.stopPropagation(); goto('Active'); }} className="text-left hover:opacity-70" data-testid={`card-${c.category.toLowerCase()}-active`}><p className="text-2xl font-bold text-green-600">{c.active}</p><p className="text-xs text-slate-400">Active</p></button>
                    <button onClick={(e) => { e.stopPropagation(); goto('Pending'); }} className="text-left hover:opacity-70" data-testid={`card-${c.category.toLowerCase()}-pending`}><p className="text-2xl font-bold text-amber-500">{c.pending}</p><p className="text-xs text-slate-400">Pending</p></button>
                    <button onClick={(e) => { e.stopPropagation(); goto('Terminated'); }} className="text-left hover:opacity-70" data-testid={`card-${c.category.toLowerCase()}-terminated`}><p className="text-2xl font-bold text-slate-500">{c.terminated ?? 0}</p><p className="text-xs text-slate-400">Terminated</p></button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Missing Documents footer */}
          <div className="bg-white rounded-xl p-6 border border-slate-200" data-testid="missing-documents-footer">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <h2 className="text-lg font-semibold text-slate-900">Missing Documents</h2>
              <span className="text-sm text-slate-400">({missing.length})</span>
            </div>
            {missing.length === 0 ? (
              <p className="text-sm text-slate-400" data-testid="no-missing-documents">No outstanding document requests. 🎉</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {missing.map((m) => {
                  const overdue = m.overdue;
                  const upcoming = !overdue && m.due_date && m.due_date <= new Date(Date.now() + 7 * 864e5).toISOString().slice(0, 10);
                  return (
                    <Link key={m.id} to={`/portal/clients/${m.client_id}`}
                      className="flex items-center justify-between gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded transition-colors"
                      data-testid={`missing-doc-${m.id}`}>
                      <div className="min-w-0">
                        <p className="text-slate-800 font-medium truncate">{m.document_type}{m.label ? ` — ${m.label}` : ''}</p>
                        <p className="text-xs text-slate-500">{m.client_name}{m.calyvra_client_id ? ` · ${m.calyvra_client_id}` : ''}</p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {m.due_date ? (
                          <Badge className={overdue ? 'bg-red-100 text-red-700' : upcoming ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'} data-testid={`missing-doc-badge-${m.id}`}>
                            <CalendarClock className="h-3 w-3 mr-1" />{overdue ? 'Overdue' : 'Due'} {m.due_date}
                          </Badge>
                        ) : <Badge className="bg-slate-100 text-slate-500">No due date</Badge>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs text-slate-500">{label}</label>
      <Select value={value || NONE} onValueChange={onChange}>
        <SelectTrigger className="w-[160px] h-9" data-testid={testid}><SelectValue placeholder={`All`} /></SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export default StandardDashboard;
